import { DatePipe } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import moment from 'moment';

import {
  AnimalInsuranceMetadata,
  HealthInsuranceMetadata,
  HouseholdMetadata,
  InsuranceMetadatas,
  LegalProtectionMetadata,
  TravelMetadata,
  VehicleMetadata,
} from '../_types/lead.types';
import { InsuranceType } from '../enums/insurance-type.enum';

const toDateWithUtcFallback = (value: string | Date): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const utcString = /Z$|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
  const date = new Date(utcString);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateToFrCh = (
  value?: string | Date | null
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = toDateWithUtcFallback(value);
  if (!date) {
    return typeof value === 'string' ? value : undefined;
  }

  return new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const formatDateTimeToFrCh = (
  value?: string | Date | null
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = toDateWithUtcFallback(value);
  if (!date) {
    return typeof value === 'string' ? value : undefined;
  }

  return new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

export const customMatcherTextHelper = (
  value: string,
  filterText: string,
  filterOption: string
) => {
  switch (filterOption) {
    case 'contains':
      return value.toUpperCase().indexOf(filterText.toUpperCase()) >= 0;
    case 'notContains':
      return value.toUpperCase().indexOf(filterText.toUpperCase()) < 0;
    case 'equals':
      return value.toUpperCase() === filterText.toUpperCase();
    case 'notEqual':
      return value.toUpperCase() != filterText.toUpperCase();
    case 'startsWith':
      return value.toUpperCase().indexOf(filterText.toUpperCase()) === 0;
    case 'endsWith':
      const index = value.toUpperCase().lastIndexOf(filterText.toUpperCase());
      return index >= 0 && index === value.length - filterText.length;
    default:
      // should never happen
      return false;
  }
};

export const getInsuranceNameFromMetadata = (
  metadata: InsuranceMetadatas,
  insuranceType: InsuranceType
): string => {
  switch (insuranceType) {
    case InsuranceType.HEALTH:
      const healthMetadata = metadata as HealthInsuranceMetadata;
      return (
        healthMetadata?.chosenBaseOffer?.insuranceName ??
        healthMetadata?.chosenComplementaryOffer?.insuranceName ??
        ''
      );
    case InsuranceType.ANIMAL:
      return (
        (metadata as AnimalInsuranceMetadata)?.chosenBaseOffer?.insuranceName ??
        ''
      );
    case InsuranceType.TRAVEL:
      return (metadata as TravelMetadata)?.chosenBaseOffer?.insuranceName ?? '';
    case InsuranceType.LEGAL_PROTECTION:
      return (
        (metadata as LegalProtectionMetadata)?.chosenBaseOffer?.insuranceName ??
        ''
      );
    case InsuranceType.MOTOR_VEHICLE:
      return (
        (metadata as VehicleMetadata)?.chosenBaseOffer?.insuranceName ?? ''
      );
    case InsuranceType.RC_HOUSEHOLD:
      return (
        (metadata as HouseholdMetadata)?.chosenBaseOffer?.insuranceName ?? ''
      );
    default:
      return '';
  }
};

export const getControl = (control: AbstractControl): FormControl =>
  control as FormControl;
export const getGroup = (control: AbstractControl): FormGroup =>
  control as FormGroup;
export const getFormArray = (control: AbstractControl): FormArray =>
  control as FormArray;

/** Re-runs every validator of the group and its subgroups, e.g. after a requiredIf condition changed. */
export const revalidate = (group: FormGroup): void => {
  for (const control of Object.values(group.controls)) {
    if (control instanceof FormGroup) revalidate(control);
    control.updateValueAndValidity({ emitEvent: false });
  }
};

/** Whether the control's validators currently ask for a value, requiredIf conditions included. */
export const isRequired = (control: AbstractControl): boolean =>
  !!control.validator?.(new FormControl(null))?.['required'];

export type EmailOrPhoneHint = 'hidden' | 'hint' | 'error';

/** State of the prospect "email or phone" hint: an error once the rule fails and either field was used. */
export function emailOrPhoneHint(
  form: AbstractControl,
  isProspect: boolean
): EmailOrPhoneHint {
  if (!isProspect) return 'hidden';
  const controlNames: string[] | undefined = form.errors?.['requiredOneOf'];
  if (!controlNames) return 'hint';
  const used = controlNames.some((name) => {
    const control = form.get(name);
    return control?.touched || control?.dirty;
  });
  return used ? 'error' : 'hint';
}

/** Sends cleared fields as absent: the API rejects empty strings for enums and unique fields. */
export const blankToNull = <T extends object>(value: T): T =>
  Object.fromEntries(
    Object.entries(value).map(([key, field]) => [
      key,
      field === '' ? null : field,
    ])
  ) as T;
export const formatDateForInput = (dateString: string): string => {
  if (!dateString) return '';
  const momentDate = moment(dateString, 'DD/MM/YYYY');
  return new DatePipe('fr').transform(momentDate.toDate(), 'yyyy-MM-dd')!;
};

export const getInsuranceStartDateFromMetadata = (
  metadata: InsuranceMetadatas,
  insuranceType: InsuranceType
): string => {
  switch (insuranceType) {
    case InsuranceType.HEALTH:
      const healthMetadata = metadata as HealthInsuranceMetadata;
      return healthMetadata.startDate ?? '';
    case InsuranceType.ANIMAL:
      return (
        (metadata as AnimalInsuranceMetadata)?.insuranceStartDate?.toString() ??
        ''
      );
    case InsuranceType.TRAVEL:
      return (metadata as TravelMetadata)?.startDate ?? '';
    case InsuranceType.LEGAL_PROTECTION:
      return (metadata as LegalProtectionMetadata)?.startDate ?? '';
    case InsuranceType.MOTOR_VEHICLE:
      return (metadata as VehicleMetadata)?.insuranceStartDate ?? '';
    case InsuranceType.RC_HOUSEHOLD:
      return (metadata as HouseholdMetadata)?.startDate ?? '';
    default:
      return '';
  }
};

export const getFileName = (docurl: string) => {
  const myarr = docurl.split('/');
  const myimg = myarr[myarr.length - 1] ? myarr[myarr.length - 1] : '';
  return myimg.split('?')[0];
};

export const noRowsOverlayMessage = $localize`Aucune donnée à afficher`;

export const isObjectEmpty = (obj: Object): boolean => {
  for (const key in obj) {
    // if the value is 'object'
    if (obj[key] instanceof Object === true) {
      if (isObjectEmpty(obj[key]) === false) return false;
    }
    // if value is string/number
    else {
      // if array or string have length is not 0.
      if (obj[key].length !== 0) return false;
    }
  }
  return true;
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getBooleanLabel = (value?: boolean | string): string => {
  if (value !== null && value !== undefined)
    return value === true || value === 'true' ? $localize`Oui` : $localize`Non`;
  return '';
};

/**
 * `yyyy-MM-dd` from local calendar parts, the value shape
 * `<input type="date">` expects.
 *
 * Deliberately not `toISOString().split('T')[0]`, which converts to UTC first
 * and so reports the previous day for anyone east of it in the evening.
 */
export const toDateInputValue = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** `yyyy-MM-ddTHH:mm`, the value shape `<input type="datetime-local">` expects. */
export const toDateTimeInputValue = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${toDateInputValue(date)}T${hours}:${minutes}`;
};

/**
 * Shifts a local `yyyy-MM-ddTHH:mm` (the `<input type="datetime-local">`
 * value) to the matching UTC wall clock, as a zone-less
 * `yyyy-MM-ddTHH:mm:ss` for a backend `LocalDateTime` that is stored in UTC.
 */
export const toUtcLocalDateTime = (localDateTime: string): string => {
  const date = new Date(localDateTime);
  if (Number.isNaN(date.getTime())) return localDateTime;
  return date.toISOString().slice(0, 19);
};

/** Placeholder shown when a value is missing. */
export const EMPTY_VALUE = '–';

export const fmtDate = (
  val: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }
): string => {
  if (!val) return EMPTY_VALUE;
  try {
    return new Date(val).toLocaleDateString(undefined, opts);
  } catch {
    return val;
  }
};

/**
 * `31.01.2026 à 14:05`, in Swiss time. A bare timestamp with no offset is
 * read as UTC, which is how the backend's history rows are serialised.
 */
export const fmtUtcDateTime = (rawDate: string | null | undefined): string => {
  if (!rawDate) return EMPTY_VALUE;
  const utcString = /Z$|[+-]\d{2}:\d{2}$/.test(rawDate)
    ? rawDate
    : `${rawDate}Z`;
  const date = new Date(utcString);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  const day = new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat('fr-CH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return `${day} à ${time}`;
};

/** A picked address, split the way the address fields store it: the street alone, beside the rest. */
export type SplitAddress = {
  street: string;
  postCode: string;
  city: string;
  state: string;
  /** ISO 3166-1 alpha-2, e.g. "CH". */
  country: string;
};

/**
 * Splits a Google place into the address fields. The street is built from the route and number
 * rather than taken from the name, which is a business's name when a business was picked; the
 * city prefers what the formatted address shows, which the components sometimes lack.
 */
export const splitGoogleAddress = (
  place: google.maps.places.PlaceResult
): SplitAddress => {
  const component = (type: string) =>
    place.address_components?.find((part) => part.types.includes(type));
  const shownCity = new DOMParser()
    .parseFromString(place.adr_address ?? '', 'text/html')
    .querySelector('.locality')?.textContent;
  const street = [
    component('route')?.long_name,
    component('street_number')?.long_name,
  ]
    .filter(Boolean)
    .join(' ');
  return {
    street: street || place.name || '',
    postCode: component('postal_code')?.long_name ?? '',
    city:
      shownCity ||
      component('locality')?.long_name ||
      component('administrative_area_level_2')?.long_name ||
      '',
    state: component('administrative_area_level_1')?.long_name ?? '',
    country: component('country')?.short_name ?? '',
  };
};

export const buildSearchParams = (
  params: Record<string, string | number | boolean | string[] | undefined>
): URLSearchParams =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      if (value.length) acc.append(key, value.join(','));
    } else if (value) {
      acc.append(key, String(value));
    }
    return acc;
  }, new URLSearchParams());

/** Case- and accent-insensitive, so "vaudoise" finds "Vaudoise Assurances". */
export const normalizeForSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
